<template>
  <v-card>
    <v-card-text class="text--primary">
      <v-container fluid>
        <v-row>
          <v-col cols="12" md="6">
            <v-row class="mb-2">
              <v-col>
                <div class="text-right">
                  <v-btn text small color="error"
                         @click="handleDeleteButtonClick"
                         :loading="progress('patients.deletePatient')"
                         :disabled="!canDeletePatient">Delete patient
                  </v-btn>
                </div>
                <confirm ref="delete-confirm"></confirm>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="9" md="8" lg="9">
                <v-text-field label="Name"
                              v-model="name"
                              outlined
                              dense
                              :error-messages="getValidationErrorsByField('name')"></v-text-field>
              </v-col>
              <v-col cols="3" md="4" lg="3">
                <v-select
                    ref="gender"
                    class="form-item ml-1"
                    v-model="gender"
                    :items="availableGenders"
                    item-text="title"
                    item-value="value"
                    outlined
                    dense
                    label="Gender"
                ></v-select>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col>
                <v-menu
                    v-model="patientBirthdayMenu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                >
                  <template v-slot:activator="{}">
                    <v-text-field
                        v-model="patientBirthdayInput"
                        :error-messages="getValidationErrorsByField('birthday')"
                        append-icon="event"
                        outlined
                        dense
                        placeholder="DD.MM.YYYY"
                        v-mask="'##.##.####'"
                        @click:append="patientBirthdayMenu = true"
                    ></v-text-field>
                  </template>
                  <v-date-picker v-model="patientBirthday"
                                 ref="patientBirthdayPicker"
                                 @input="patientBirthdayMenu = false"
                                 first-day-of-week="1"
                                 :locale="$i18n.locale"
                                 :min="minBirthday"
                                 show-current="1980-01-01"
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col>
                <v-text-field label="Email"
                              v-model="email"
                              outlined
                              dense
                              :error-messages="getValidationErrorsByField('email')"></v-text-field>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col>
                <v-text-field label="Phone"
                              v-model="phone"
                              outlined
                              dense
                              :error-messages="getValidationErrorsByField('phone')"></v-text-field>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col>
                <v-text-field label="Address"
                              v-model="address"
                              outlined
                              dense
                              :error-messages="getValidationErrorsByField('address')"></v-text-field>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col>
                <v-textarea
                    v-model="comment"
                    label="Comment"
                    rows="3"
                    outlined
                    dense
                ></v-textarea>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col>
                <div class="text-right">
                  <v-spacer></v-spacer>
                  <v-btn color="success" @click="handleSaveButtonClick"
                         :loading="progress('patients.saveEditedPatient')"
                         :disabled="progress('patients.saveEditedPatient')">
                    Save
                  </v-btn>
                  <v-btn class="ml-2" @click="handleCancelButtonClick">
                    Cancel
                  </v-btn>
                </div>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
import Confirm from '../../Common/Confirm'
import {reformatDate} from '@/helpers/timeformats'
import {mapActions, mapGetters, mapState} from 'vuex'
import moment from 'moment'

export default {
  name: 'UpdatePatient',
  props: {
    patient: Object
  },
  components: {
    Confirm
  },
  data() {
    return {
      patientBirthdayMenu: false
    }
  },
  watch: {
    patientBirthdayMenu(val) {
      val && setTimeout(() => (this.$refs.patientBirthdayPicker.activePicker = 'YEAR'))
    },
  },
  computed: {
    ...mapState({
      availableGenders: 'availableGenders'
    }),
    ...mapState('patients', {
      editedPatient: 'editedPatient',
    }),
    ...mapGetters('patients', {}),
    ...mapGetters({
      progress: 'progress'
    }),
    canDeletePatient() {
      return _.get(this.editedPatient, ['data', 'patientCases'], []).length === 0
    },
    minBirthday() {
      return moment().subtract(100, 'years').format('YYYY-MM-DD')
    },
    name: {
      get() {
        return this.editedPatient.data.name
      },
      set(value) {
        this.setEditedPatientFieldDebounced('name', value)
      }
    },
    gender: {
      get() {
        return this.editedPatient.data.gender
      },
      set(value) {
        this.setEditedPatientFieldDebounced('gender', value)
      }
    },
    patientBirthday: {
      get() {
        return _.get(this.editedPatient, ['data', 'birthday'], null)
      },
      set(value) {
        this.setEditedPatientField('birthday', value)
      }
    },
    patientBirthdayInput: {
      get() {
        return reformatDate(this.patientBirthday)
      },
      set(value) {
        const date = moment(value, "DD.MM.YYYY", true)
        if (!date.isValid()) {
          return
        }

        this.patientBirthday = date.format('YYYY-MM-DD')
      }
    },
    email: {
      get() {
        return this.editedPatient.data.email
      },
      set(value) {
        this.setEditedPatientFieldDebounced('email', value)
      }
    },
    phone: {
      get() {
        return this.editedPatient.data.phone
      },
      set(value) {
        this.setEditedPatientFieldDebounced('phone', value)
      }
    },
    address: {
      get() {
        return this.editedPatient.data.address
      },
      set(value) {
        this.setEditedPatientFieldDebounced('address', value)
      }
    },
    comment: {
      get() {
        return this.editedPatient.data.comment
      },
      set(value) {
        this.setEditedPatientFieldDebounced('comment', value)
      }
    }
  },
  mounted() {
  },
  methods: {
    ...mapActions('patients', {
      updateEditedPatientField: 'updateEditedPatientField',
      deletePatient: 'deletePatient',
      saveEditedPatient: 'saveEditedPatient'
    }),
    getValidationErrorsByField(fieldName) {
      return _.get(this.editedPatient, ['validationErrorsByFields', fieldName], [])
    },
    async handleSaveButtonClick() {
      this.saveEditedPatient()
    },
    handleCancelButtonClick() {
      this.$router.push(`/patients/${this.editedPatient.data.id}`)
    },
    handleDeleteButtonClick() {
      this.$refs['delete-confirm'].open('Delete patient', `Do you want to delete patient: "${this.editedPatient.data.name}"?`, {width: 600}).then(async (confirm) => {
        if (!confirm) {
          return
        }

        try {
          let result = await this.deletePatient(this.editedPatient.data)

          if (result) {
            this.$router.push('/patients')
          }
        } catch (e) {
          console.log(e)
        }
      })
    },
    setEditedPatientFieldDebounced: _.debounce(function (fieldName, value) {
      return this.setEditedPatientField(fieldName, value)
    }, 200),
    setEditedPatientField: function (fieldName, value) {
      let resultValue = _.cloneDeep(value)
      if (value === '') {
        resultValue = null
      }

      this.updateEditedPatientField({
        fieldName: fieldName,
        value: resultValue,
      })
    },
    reformatDate: reformatDate
  }
}
</script>

<style scoped>

</style>